import { createSlice } from '@reduxjs/toolkit';
import { produce } from 'immer';

const MAX_HISTORY = 50;

const initialState = {
  currentTemplate: null,
  templateData: null,
  past: [],
  future: [],
  isDirty: false,
  validationErrors: [],
  validationWarnings: [],
  stats: {
    sections: 0,
    fields: 0,
    formulas: 0,
    jsonSize: 0,
  },
};

const rfpTemplateSlice = createSlice({
  name: 'rfpTemplate',
  initialState,
  reducers: {
    // Load template
    setTemplate: (state, action) => {
      state.currentTemplate = action.payload;
      state.templateData = action.payload.template_data;
      state.past = [];
      state.future = [];
      state.isDirty = false;
    },

    // Update template data with undo/redo support
    updateTemplateData: (state, action) => {
      const newData = action.payload;

      // Save current state to history
      if (state.templateData) {
        state.past = [...state.past, state.templateData].slice(-MAX_HISTORY);
      }

      state.templateData = newData;
      state.future = [];
      state.isDirty = true;
    },

    // Update specific field path
    updateField: (state, action) => {
      const { path, value } = action.payload;

      // Save current state
      state.past = [...state.past, state.templateData].slice(-MAX_HISTORY);

      // Update using immer
      state.templateData = produce(state.templateData, (draft) => {
        const pathArray = path.split('.');
        let current = draft;

        for (let i = 0; i < pathArray.length - 1; i++) {
          const key = pathArray[i];
          if (current[key] === undefined) {
            current[key] = {};
          }
          current = current[key];
        }

        current[pathArray[pathArray.length - 1]] = value;
      });

      state.future = [];
      state.isDirty = true;
    },

    // Undo
    undo: (state) => {
      if (state.past.length === 0) return;

      const previous = state.past[state.past.length - 1];
      state.future = [state.templateData, ...state.future].slice(0, MAX_HISTORY);
      state.templateData = previous;
      state.past = state.past.slice(0, -1);
      state.isDirty = true;
    },

    // Redo
    redo: (state) => {
      if (state.future.length === 0) return;

      const next = state.future[0];
      state.past = [...state.past, state.templateData].slice(-MAX_HISTORY);
      state.templateData = next;
      state.future = state.future.slice(1);
      state.isDirty = true;
    },

    // Clear history (after save)
    clearHistory: (state) => {
      state.past = [];
      state.future = [];
      state.isDirty = false;
    },

    // Validation
    setValidation: (state, action) => {
      const { errors, warnings, stats } = action.payload;
      state.validationErrors = errors || [];
      state.validationWarnings = warnings || [];
      state.stats = stats || state.stats;
    },

    // Reset
    resetTemplate: () => initialState,
  },
});

export const {
  setTemplate,
  updateTemplateData,
  updateField,
  undo,
  redo,
  clearHistory,
  setValidation,
  resetTemplate,
} = rfpTemplateSlice.actions;

// Selectors
export const selectCanUndo = (state) => state.rfpTemplate.past.length > 0;
export const selectCanRedo = (state) => state.rfpTemplate.future.length > 0;
export const selectIsDirty = (state) => state.rfpTemplate.isDirty;
export const selectTemplateData = (state) => state.rfpTemplate.templateData;
export const selectCurrentTemplate = (state) => state.rfpTemplate.currentTemplate;
export const selectValidation = (state) => ({
  errors: state.rfpTemplate.validationErrors,
  warnings: state.rfpTemplate.validationWarnings,
  stats: state.rfpTemplate.stats,
});

export default rfpTemplateSlice.reducer;
